// src/hooks/useSnippets.js
// Cache global: un solo listener de Firestore compartido entre todos los componentes.
import { useState, useEffect, useRef } from 'react';
import { subscribeToSnippets, getUserWorkspace } from '../services/firestore';

const cache = {
  workspaceId: null,
  snippets: [],
  listeners: 0,
  unsubscribe: null,
  subscribers: new Set(),
};

const notifySubscribers = (snippets) => {
  cache.snippets = snippets;
  cache.subscribers.forEach(cb => cb(snippets));
};

const startListener = async (userId) => {
  const workspaceId = await getUserWorkspace(userId);
  if (cache.unsubscribe && cache.workspaceId === workspaceId) return;
  if (cache.unsubscribe) cache.unsubscribe();

  cache.workspaceId = workspaceId;
  cache.unsubscribe = subscribeToSnippets(workspaceId, notifySubscribers);
};

const stopListener = () => {
  if (cache.unsubscribe) {
    cache.unsubscribe();
    cache.unsubscribe = null;
    cache.workspaceId = null;
    cache.snippets = [];
  }
};

/**
 * Hook para manejar los snippets del usuario.
 * Un listener compartido carga TODOS los snippets del workspace.
 */
export const useSnippets = (userId) => {
  const [snippets, setSnippets] = useState(cache.snippets);
  const [loading, setLoading] = useState(cache.snippets.length === 0);
  const callbackRef = useRef(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const callback = (updated) => {
      setSnippets(updated);
      setLoading(false);
    };
    callbackRef.current = callback;
    cache.subscribers.add(callback);
    cache.listeners++;

    if (cache.snippets.length > 0) {
      setSnippets(cache.snippets);
      setLoading(false);
    }

    startListener(userId).catch(err => {
      console.error('Error al suscribirse a snippets:', err);
      setLoading(false);
    });

    return () => {
      cache.subscribers.delete(callback);
      cache.listeners--;
      if (cache.listeners <= 0) {
        cache.listeners = 0;
        stopListener();
      }
    };
  }, [userId]);

  return { snippets, loading };
};
